import * as Y from "yjs";

// Immutable empty updates, generated once with Y.encodeStateAsUpdate on five
// independent documents. Never regenerate these or reuse their client IDs for
// edits. Applying identical updates gives concurrent clients the SAME nested
// types without changing any live document's client ID. Each field has its own
// update so adding a missing field cannot replace an existing legacy field.
const FIELDS = [
  ["vaultIndex", "folders", [1,1,178,225,139,245,7,0,39,1,10,118,97,117,108,116,73,110,100,101,120,7,102,111,108,100,101,114,115,1,0]],
  ["vaultIndex", "docs", [1,1,135,138,238,138,13,0,39,1,10,118,97,117,108,116,73,110,100,101,120,4,100,111,99,115,1,0]],
  ["vaultIndex", "keypair", [1,1,180,172,228,231,1,0,39,1,10,118,97,117,108,116,73,110,100,101,120,7,107,101,121,112,97,105,114,1,0]],
  ["preferences", "calendarConfig", [1,1,138,249,139,183,9,0,39,1,11,112,114,101,102,101,114,101,110,99,101,115,14,99,97,108,101,110,100,97,114,67,111,110,102,105,103,1,0]],
  ["vaultIndex", "metadata", [1,1,166,177,199,232,9,0,39,1,10,118,97,117,108,116,73,110,100,101,120,8,109,101,116,97,100,97,116,97,1,0]],
];

export function ensureVaultStructure(doc) {
  doc.transact(() => {
    for (const [root, key, bytes] of FIELDS) {
      if (!doc.getMap(root).has(key)) Y.applyUpdate(doc, new Uint8Array(bytes), "local");
    }
  }, "local");
}

// Older versions independently allocated the nested maps. Those structures
// cannot be merged by Yjs without hiding one subtree. Reject that update BEFORE
// applying it; both local storage and the encrypted server source remain intact
// and the sync manager disables publishing/compaction. Ordinary legacy vaults
// retain their identities; compatible updates do not require rebuilding them.
export function assertCompatibleVaultStructure(doc, update) {
  const fields = new Set([...FIELDS.map(([root, key]) => `${root}/${key}`), "vaultIndex/maps", "vaultIndex/sharedSubtrees"]);
  const sources = [Y.decodeUpdate(Y.encodeStateAsUpdate(doc)), Y.decodeUpdate(update)];
  const identities = new Map();
  const dependents = new Map();
  // Yjs can compact consecutive deleted assignments into a single struct.
  // Resolve origins within those ranges without expanding potentially huge
  // clock ranges or depending on the private Y.Map linked list.
  const indexes = sources.map(source => {
    const clients = new Map();
    for (const item of source.structs) {
      if (!clients.has(item.id.client)) clients.set(item.id.client, []);
      clients.get(item.id.client).push(item);
    }
    for (const items of clients.values()) items.sort((a, b) => a.id.clock - b.id.clock);
    return clients;
  });
  const idKey = id => `${id.client}:${id.clock}`;
  const originIds = origin => {
    const ids = new Set([idKey(origin)]);
    for (const index of indexes) {
      const items = index.get(origin.client) || [];
      let low = 0, high = items.length;
      while (low < high) {
        const mid = (low + high) >>> 1;
        if (items[mid].id.clock <= origin.clock) low = mid + 1;
        else high = mid;
      }
      const item = items[low - 1];
      if (item && origin.clock < item.id.clock + item.length) ids.add(idKey(item.id));
    }
    return ids;
  };
  const deleted = (source, id) => source.ds.clients.get(id.client)?.some(
    range => id.clock >= range.clock && id.clock < range.clock + range.len,
  ) || false;

  // Resolve field identities even through deleted historical assignments. An
  // update can encode its parent by referring to an earlier assignment, and
  // structs from different clients are not necessarily in causal order.
  for (const source of sources) {
    for (const item of source.structs) {
      if (!item.id) continue;
      const id = idKey(item.id);
      const field = typeof item.parent === "string" ? `${item.parent}/${item.parentSub}` : null;
      if (fields.has(field)) identities.set(id, field);
      else if (item.parent === null) {
        for (const origin of [item.origin, item.rightOrigin]) {
          if (!origin) continue;
          for (const parentId of originIds(origin)) {
            if (!dependents.has(parentId)) dependents.set(parentId, new Set());
            dependents.get(parentId).add(id);
          }
        }
      }
    }
  }
  const queue = [...identities.keys()];
  for (let i = 0; i < queue.length; i++) {
    const id = queue[i];
    for (const child of dependents.get(id) || []) {
      if (!identities.has(child)) {
        identities.set(child, identities.get(id));
        queue.push(child);
      }
    }
  }

  const roots = new Map();
  for (let sourceIndex = 0; sourceIndex < sources.length; sourceIndex++) {
    const source = sources[sourceIndex];
    for (const item of source.structs) {
      if (!item.id) continue;
      const id = idKey(item.id), field = identities.get(id);
      if (!field || deleted(source, item.id)) continue;
      // A stale peer may resend a root that this client already deleted. Yjs
      // will ignore it too. Keep current live roots in the comparison even if
      // the incoming update deletes them: replacing one can hide local edits.
      if (sourceIndex === 1 && deleted(sources[0], item.id)) continue;
      const previous = roots.get(field);
      if (previous && previous !== id) {
        const error = new Error("Incompatible vault initialization: sync paused to preserve both vaults. Keep both devices' data for recovery.");
        error.code = "VAULT_STRUCTURE_CONFLICT";
        throw error;
      }
      roots.set(field, id);
    }
  }
}
